import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, X, Check, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { LeadStatus, LeadSource, LeadTag, LeadCustomField, useLeadMasters } from './leads.api';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Leads → Settings: the four masters the module is configured from.
 *
 * Status Master · Custom Lead Columns · Source Master · Tags Master.
 *
 * The server refuses to delete a status or source that leads still point at, and says how many —
 * deleting one out from under 200 leads would strand them off the pipeline board. Switching a row
 * off is the safe equivalent and is offered in the same place.
 */
const TABS = ['Status', 'Custom Columns', 'Source', 'Tags'] as const;
type Tab = typeof TABS[number];

const inputCls = 'border rounded px-2 py-1.5 text-sm dark:bg-slate-900 dark:border-slate-700';

export function LeadsSettingsPage() {
  const qc = useQueryClient();
  const { data: masters, isLoading } = useLeadMasters();
  const [tab, setTab] = useState<Tab>('Status');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['lead-masters'] });
    qc.invalidateQueries({ queryKey: ['lead-pipeline'] });
    qc.invalidateQueries({ queryKey: ['leads'] });
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Lead Settings</h1>
        <p className="text-xs text-gray-500 mt-0.5">Statuses, custom columns, sources and tags — the lists every lead screen picks from.</p>
      </div>

      <LeadsSubNav />

      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 mb-4 w-fit">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 text-sm rounded-md ${tab === t ? 'bg-white dark:bg-slate-900 shadow-sm text-emerald-700 font-medium' : 'text-slate-600 dark:text-slate-300'}`}>
            {t} Master
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : tab === 'Status' ? (
        <StatusMaster rows={masters?.statuses ?? []} onChanged={refresh} />
      ) : tab === 'Custom Columns' ? (
        <FieldMaster rows={masters?.fields ?? []} onChanged={refresh} />
      ) : tab === 'Source' ? (
        <SourceMaster rows={masters?.sources ?? []} onChanged={refresh} />
      ) : (
        <TagMaster rows={masters?.tags ?? []} onChanged={refresh} />
      )}
    </div>
  );
}

/** Shared save/delete plumbing — every master is the same four calls against a different path. */
function useMaster(path: string, onChanged: () => void) {
  const save = useMutation({
    mutationFn: async (v: { id?: number | null; body: any }) =>
      v.id ? (await api.patch(`/leads/masters/${path}/${v.id}`, v.body)).data : (await api.post(`/leads/masters/${path}`, v.body)).data,
    onSuccess: () => { toast.success('Saved'); onChanged(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save'),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/leads/masters/${path}/${id}`)).data,
    onSuccess: () => { toast.success('Deleted'); onChanged(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete'),
  });
  return { save, remove };
}

function StatusMaster({ rows, onChanged }: { rows: LeadStatus[]; onChanged: () => void }) {
  const { save, remove } = useMaster('statuses', onChanged);
  const [edit, setEdit] = useState<Partial<LeadStatus> | null>(null);

  const reorder = useMutation({
    mutationFn: async (ids: number[]) => (await api.post('/leads/masters/statuses/reorder', { ids })).data,
    onSuccess: onChanged,
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not reorder'),
  });

  const moveRow = (i: number, dir: -1 | 1) => {
    const next = [...rows];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    reorder.mutate(next.map((r) => r.id));
  };

  return (
    <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
      <div className="px-4 py-2 border-b flex items-center justify-between bg-slate-50 dark:bg-slate-800">
        <div>
          <h2 className="text-sm font-semibold">Status Master</h2>
          <p className="text-[11px] text-gray-500">Order here is the order of the columns on the pipeline board.</p>
        </div>
        <button onClick={() => setEdit({ name: '', colour: '#3b82f6', isActive: true })}
          className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1"><Plus size={13} /> Add status</button>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-gray-500">
          <tr>
            <th className="text-left px-3 py-2">Order</th>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Colour</th>
            <th className="text-left px-3 py-2">Marks</th>
            <th className="text-left px-3 py-2">Active</th>
            <th className="text-left px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={s.id} className="border-t">
              <td className="px-3 py-2">
                <div className="flex items-center gap-1">
                  <button onClick={() => moveRow(i, -1)} disabled={i === 0} className="p-0.5 border rounded disabled:opacity-30"><ArrowUp size={11} /></button>
                  <button onClick={() => moveRow(i, 1)} disabled={i === rows.length - 1} className="p-0.5 border rounded disabled:opacity-30"><ArrowDown size={11} /></button>
                </div>
              </td>
              <td className="px-3 py-2 font-medium">{s.name}{s.isDefault && <span className="ml-2 text-[10px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded px-1">default</span>}</td>
              <td className="px-3 py-2"><span className="inline-block w-4 h-4 rounded" style={{ background: s.colour ?? '#cbd5e1' }} /></td>
              <td className="px-3 py-2 text-xs">{s.isWon ? 'Won' : s.isLost ? 'Lost' : '—'}</td>
              <td className="px-3 py-2 text-xs">{s.isActive ? 'Yes' : 'No'}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  <button onClick={() => setEdit(s)} className="p-1 border rounded"><Pencil size={12} /></button>
                  <button onClick={() => { if (window.confirm(`Delete status "${s.name}"?`)) remove.mutate(s.id); }}
                    className="p-1 border rounded text-rose-600 border-rose-200"><Trash2 size={12} /></button>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={6} className="text-center py-6 text-gray-400 text-xs">No statuses yet.</td></tr>}
        </tbody>
      </table>

      {edit && (
        <MasterModal title={edit.id ? 'Edit status' : 'Add status'} onClose={() => setEdit(null)}
          onSave={() => save.mutate({ id: edit.id ?? null, body: edit }, { onSuccess: () => setEdit(null) })}>
          <label className="block"><span className="text-xs text-gray-500">Name</span>
            <input className={`${inputCls} w-full`} value={edit.name ?? ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></label>
          <label className="block"><span className="text-xs text-gray-500">Colour</span>
            <input type="color" className="w-full h-9 border rounded" value={edit.colour ?? '#3b82f6'} onChange={(e) => setEdit({ ...edit, colour: e.target.value })} /></label>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!edit.isWon} onChange={(e) => setEdit({ ...edit, isWon: e.target.checked, isLost: e.target.checked ? false : edit.isLost })} /> Counts as Won</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!edit.isLost} onChange={(e) => setEdit({ ...edit, isLost: e.target.checked, isWon: e.target.checked ? false : edit.isWon })} /> Counts as Lost</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!edit.isDefault} onChange={(e) => setEdit({ ...edit, isDefault: e.target.checked })} /> Default for new leads</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={edit.isActive !== false} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Active</label>
          </div>
          <p className="text-[11px] text-gray-400">
            Won / Lost drive the funnel and the win rate — a stage that ends the deal should be marked, or the reports count it as still open.
          </p>
        </MasterModal>
      )}
    </div>
  );
}

function SourceMaster({ rows, onChanged }: { rows: LeadSource[]; onChanged: () => void }) {
  const { save, remove } = useMaster('sources', onChanged);
  const [edit, setEdit] = useState<Partial<LeadSource> | null>(null);
  return (
    <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
      <div className="px-4 py-2 border-b flex items-center justify-between bg-slate-50 dark:bg-slate-800">
        <div>
          <h2 className="text-sm font-semibold">Source Master</h2>
          <p className="text-[11px] text-gray-500">Where a lead came from — this is what the By Source report groups on.</p>
        </div>
        <button onClick={() => setEdit({ name: '', isActive: true })} className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1"><Plus size={13} /> Add source</button>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-gray-500"><tr>
          <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Order</th><th className="text-left px-3 py-2">Active</th><th className="text-left px-3 py-2">Actions</th>
        </tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="border-t">
              <td className="px-3 py-2 font-medium">{s.name}</td>
              <td className="px-3 py-2 text-xs">{s.sortOrder}</td>
              <td className="px-3 py-2 text-xs">{s.isActive ? 'Yes' : 'No'}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  <button onClick={() => setEdit(s)} className="p-1 border rounded"><Pencil size={12} /></button>
                  <button onClick={() => { if (window.confirm(`Delete source "${s.name}"?`)) remove.mutate(s.id); }} className="p-1 border rounded text-rose-600 border-rose-200"><Trash2 size={12} /></button>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={4} className="text-center py-6 text-gray-400 text-xs">No sources yet.</td></tr>}
        </tbody>
      </table>
      {edit && (
        <MasterModal title={edit.id ? 'Edit source' : 'Add source'} onClose={() => setEdit(null)}
          onSave={() => save.mutate({ id: edit.id ?? null, body: edit }, { onSuccess: () => setEdit(null) })}>
          <label className="block"><span className="text-xs text-gray-500">Name</span>
            <input className={`${inputCls} w-full`} value={edit.name ?? ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></label>
          <label className="block"><span className="text-xs text-gray-500">Sort order</span>
            <input type="number" className={`${inputCls} w-full`} value={edit.sortOrder ?? 0} onChange={(e) => setEdit({ ...edit, sortOrder: Number(e.target.value) })} /></label>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={edit.isActive !== false} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Active</label>
        </MasterModal>
      )}
    </div>
  );
}

function TagMaster({ rows, onChanged }: { rows: LeadTag[]; onChanged: () => void }) {
  const { save, remove } = useMaster('tags', onChanged);
  const [edit, setEdit] = useState<Partial<LeadTag> | null>(null);
  return (
    <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
      <div className="px-4 py-2 border-b flex items-center justify-between bg-slate-50 dark:bg-slate-800">
        <div>
          <h2 className="text-sm font-semibold">Tags Master</h2>
          <p className="text-[11px] text-gray-500">Free labels a lead can carry any number of — filterable on Manage Leads.</p>
        </div>
        <button onClick={() => setEdit({ name: '', colour: '#6366f1', isActive: true })} className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1"><Plus size={13} /> Add tag</button>
      </div>
      <div className="p-3 flex flex-wrap gap-2">
        {rows.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-2 border rounded-full pl-3 pr-1 py-1 text-xs"
            style={{ borderColor: t.colour ?? '#cbd5e1', color: t.colour ?? '#475569' }}>
            {t.name}{!t.isActive && <span className="text-gray-400">(off)</span>}
            <button onClick={() => setEdit(t)} className="p-0.5 hover:bg-slate-100 rounded-full"><Pencil size={11} /></button>
            <button onClick={() => { if (window.confirm(`Delete tag "${t.name}"? It will be removed from every lead that carries it.`)) remove.mutate(t.id); }}
              className="p-0.5 hover:bg-rose-50 rounded-full text-rose-500"><X size={11} /></button>
          </span>
        ))}
        {!rows.length && <span className="text-xs text-gray-400">No tags yet.</span>}
      </div>
      {edit && (
        <MasterModal title={edit.id ? 'Edit tag' : 'Add tag'} onClose={() => setEdit(null)}
          onSave={() => save.mutate({ id: edit.id ?? null, body: edit }, { onSuccess: () => setEdit(null) })}>
          <label className="block"><span className="text-xs text-gray-500">Name</span>
            <input className={`${inputCls} w-full`} value={edit.name ?? ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} autoFocus /></label>
          <label className="block"><span className="text-xs text-gray-500">Colour</span>
            <input type="color" className="w-full h-9 border rounded" value={edit.colour ?? '#6366f1'} onChange={(e) => setEdit({ ...edit, colour: e.target.value })} /></label>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={edit.isActive !== false} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Active</label>
        </MasterModal>
      )}
    </div>
  );
}

function FieldMaster({ rows, onChanged }: { rows: LeadCustomField[]; onChanged: () => void }) {
  const { save, remove } = useMaster('fields', onChanged);
  const [edit, setEdit] = useState<any>(null);
  return (
    <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
      <div className="px-4 py-2 border-b flex items-center justify-between bg-slate-50 dark:bg-slate-800">
        <div>
          <h2 className="text-sm font-semibold">Custom Lead Columns</h2>
          <p className="text-[11px] text-gray-500">Extra fields on the lead form, without a code change. Tick &ldquo;show in list&rdquo; to add it as a column on Manage Leads.</p>
        </div>
        <button onClick={() => setEdit({ label: '', fieldType: 'TEXT', showInList: true, isActive: true, options: '' })}
          className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1"><Plus size={13} /> Add column</button>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-gray-500"><tr>
          <th className="text-left px-3 py-2">Label</th><th className="text-left px-3 py-2">Key</th><th className="text-left px-3 py-2">Type</th>
          <th className="text-left px-3 py-2">Choices</th><th className="text-left px-3 py-2">In list</th><th className="text-left px-3 py-2">Active</th><th className="text-left px-3 py-2">Actions</th>
        </tr></thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id} className="border-t">
              <td className="px-3 py-2 font-medium">{f.label}{f.isRequired && <span className="text-rose-500"> *</span>}</td>
              <td className="px-3 py-2 font-mono text-[11px] text-gray-500">{f.key}</td>
              <td className="px-3 py-2 text-xs">{f.fieldType}</td>
              <td className="px-3 py-2 text-xs text-gray-500 max-w-[220px] truncate">{(f.options ?? []).join(', ') || '—'}</td>
              <td className="px-3 py-2 text-xs">{f.showInList ? 'Yes' : 'No'}</td>
              <td className="px-3 py-2 text-xs">{f.isActive ? 'Yes' : 'No'}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  <button onClick={() => setEdit({ ...f, options: (f.options ?? []).join(', ') })} className="p-1 border rounded"><Pencil size={12} /></button>
                  <button onClick={() => { if (window.confirm(`Delete column "${f.label}"? Values already captured stay on the leads.`)) remove.mutate(f.id); }}
                    className="p-1 border rounded text-rose-600 border-rose-200"><Trash2 size={12} /></button>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={7} className="text-center py-6 text-gray-400 text-xs">No custom columns yet.</td></tr>}
        </tbody>
      </table>
      {edit && (
        <MasterModal title={edit.id ? 'Edit column' : 'Add column'} onClose={() => setEdit(null)}
          onSave={() => save.mutate({ id: edit.id ?? null, body: { ...edit, options: String(edit.options ?? '').split(',') } }, { onSuccess: () => setEdit(null) })}>
          <label className="block"><span className="text-xs text-gray-500">Label</span>
            <input className={`${inputCls} w-full`} value={edit.label ?? ''} onChange={(e) => setEdit({ ...edit, label: e.target.value })} autoFocus /></label>
          <label className="block"><span className="text-xs text-gray-500">Type</span>
            <select className={`${inputCls} w-full`} value={edit.fieldType} onChange={(e) => setEdit({ ...edit, fieldType: e.target.value })}>
              {['TEXT', 'NUMBER', 'DATE', 'SELECT', 'CHECKBOX'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select></label>
          {edit.fieldType === 'SELECT' && (
            <label className="block"><span className="text-xs text-gray-500">Choices (comma separated)</span>
              <input className={`${inputCls} w-full`} value={edit.options ?? ''} onChange={(e) => setEdit({ ...edit, options: e.target.value })} /></label>
          )}
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!edit.isRequired} onChange={(e) => setEdit({ ...edit, isRequired: e.target.checked })} /> Required</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={edit.showInList !== false} onChange={(e) => setEdit({ ...edit, showInList: e.target.checked })} /> Show in list</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={edit.isActive !== false} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Active</label>
          </div>
          {edit.id && <p className="text-[11px] text-gray-400">The key <code>{edit.key}</code> cannot be changed — every value already saved is stored under it.</p>}
        </MasterModal>
      )}
    </div>
  );
}

function MasterModal({ title, children, onClose, onSave }: { title: string; children: React.ReactNode; onClose: () => void; onSave: () => void }) {
  return (
    <div className="fixed inset-0 z-[900] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button onClick={onSave} className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1"><Check size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}
